// React
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";

// @mui
import { Card, CircularProgress, Fade, Grid, Icon, InputLabel, Modal, Tooltip } from "@mui/material";

// Apollo
import { gql, useMutation, useQuery } from "@apollo/client";

// UI
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

// Context
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "95%", sm: "80%", md: "70%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalProductosPresupuesto({ open, handleClose, idPresupuesto, onChanged }) {
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);

  const { data, loading, refetch } = useQuery(
    gql`
      query presupuestoEventoDetalles($filter: JSON) {
        presupuestoEventoDetalles(filter: $filter) {
          id
          nombre
          producto { id nombre precioCarta }
          cantidadAsignada
          cantidadUtilizada
          precioUnitario
        }
      }
    `,
    {
      variables: { filter: { idPresupuesto: idPresupuesto || null } },
      fetchPolicy: "network-only",
      skip: !open || !idPresupuesto,
    }
  );

  const [saveDetalle, { loading: saving }] = useMutation(
    gql`
      mutation savePresupuestoEventoDetalle($input: PresupuestoEventoDetalleInput!) {
        savePresupuestoEventoDetalle(input: $input) { id }
      }
    `
  );

  const [deleteDetalle] = useMutation(
    gql`
      mutation deletePresupuestoEventoDetalle($id: ID!) { deletePresupuestoEventoDetalle(id: $id) }
    `
  );

  const items = useMemo(() => data?.presupuestoEventoDetalles || [], [data]);
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    if (open) setDrafts({});
  }, [open]);

  const onChangeField = (id, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleSaveRow = async (row) => {
    const draft = drafts[row.id] || {};
    const input = {
      id: row.id,
      cantidadAsignada: draft.cantidadAsignada != null ? Number(draft.cantidadAsignada) : row.cantidadAsignada,
      precioUnitario: draft.precioUnitario != null ? Number(draft.precioUnitario) : row.precioUnitario,
    };
    try {
      await saveDetalle({ variables: { input } });
      handleSnackbar("Detalle actualizado", "success");
      await refetch();
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      handleSnackbar("Error al actualizar detalle", "error");
    }
  };

  const handleDeleteRow = async (row) => {
    confirmar({
      title: "Eliminar detalle",
      message: `¿Eliminar ${row?.producto?.nombre || "producto"} del presupuesto?`,
      icon: "warning",
      func: async () => {
        try {
          await deleteDetalle({ variables: { id: row.id } });
          handleSnackbar("Detalle eliminado", "success");
          await refetch();
          if (typeof onChanged === "function") onChanged();
        } catch (e) {
          handleSnackbar("Error al eliminar detalle", "error");
        }
      },
    });
  };

  return (
    <Modal aria-labelledby="modal-productos-presupuesto" open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftTypography variant="h6">Productos asignados</SoftTypography>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>

            {loading ? (
              <SoftBox py={4} display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </SoftBox>
            ) : (
              <SoftBox py={2}>
                <Grid container spacing={2}>
                  {items.length === 0 ? (
                    <Grid item xs={12}>
                      <SoftTypography variant="button" color="text">
                        No hay productos asignados.
                      </SoftTypography>
                    </Grid>
                  ) : (
                    items.map((row) => {
                      const draft = drafts[row.id] || {};
                      const q = draft.cantidadAsignada ?? row.cantidadAsignada ?? 0;
                      const p = draft.precioUnitario ?? row.precioUnitario ?? 0;
                      return (
                        <Grid key={row.id} item xs={12}>
                          <Card>
                            <SoftBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                              <SoftBox>
                                <SoftTypography variant="subtitle2">
                                  {row?.nombre || row?.producto?.nombre || `Detalle #${row?.id}`}
                                </SoftTypography>
                                <SoftTypography variant="caption" color="text">
                                  Usado: {row?.cantidadUtilizada ?? 0}
                                </SoftTypography>
                              </SoftBox>
                              <SoftBox display="flex" alignItems="center" gap={2}>
                                <SoftBox>
                                  <InputLabel htmlFor={`cantidad-${row.id}`}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                      Cantidad asignada
                                    </SoftTypography>
                                  </InputLabel>
                                  <SoftInput
                                    type="number"
                                    name={`cantidad-${row.id}`}
                                    value={q}
                                    onChange={(e) => onChangeField(row.id, "cantidadAsignada", e.target.value)}
                                    sx={{ width: 120 }}
                                  />
                                </SoftBox>
                                <SoftBox>
                                  <InputLabel htmlFor={`precio-${row.id}`}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                      Precio unitario
                                    </SoftTypography>
                                  </InputLabel>
                                  <SoftInput
                                    type="number"
                                    name={`precio-${row.id}`}
                                    value={p}
                                    onChange={(e) => onChangeField(row.id, "precioUnitario", e.target.value)}
                                    sx={{ width: 140 }}
                                  />
                                </SoftBox>
                                <Tooltip title="Guardar cambios" placement="top">
                                  <SoftButton color="uproGreen" circular disabled={saving} onClick={() => handleSaveRow(row)}>
                                    {saving ? <CircularProgress size={14} color="inherit" /> : <Icon>save</Icon>}
                                  </SoftButton>
                                </Tooltip>
                                <Tooltip title="Eliminar" placement="top">
                                  <SoftButton color="uproPink" circular onClick={() => handleDeleteRow(row)}>
                                    <Icon>delete</Icon>
                                  </SoftButton>
                                </Tooltip>
                              </SoftBox>
                            </SoftBox>
                          </Card>
                        </Grid>
                      );
                    })
                  )}
                </Grid>
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalProductosPresupuesto.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idPresupuesto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChanged: PropTypes.func,
};

